// src/components/landing/ProofStrip.jsx
import React, { useEffect, useRef, useState } from 'react';
import Placeholder from './Placeholder';
import { useReveal, prefersReducedMotion } from '../../utils/reveal';

// Every proof slot is a placeholder until it can be backed by a verified figure.
// To switch one on, give it a real `value` and a `label` — it then counts up
// once on scroll-in. Nothing here should ever be filled with an estimate.
const METRICS = [
  { placeholder: 'METRIC: N real projects' },
  { placeholder: 'METRIC: M DCR jurisdictions encoded' },
  { placeholder: 'METRIC: design-to-check in hours, not weeks' },
];
const LOGOS = ['LOGO: Payal Properties', 'LOGO: Tri-OM', 'LOGO: Seawoods Garden'];

/**
 * A number that counts up once when it scrolls into view.
 *
 * Screen readers get the final figure from a visually hidden copy; the counting
 * copy is hidden from them, so nobody hears "0, 3, 7, 12".
 */
function CountUp({ value, suffix = '' }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(() => (prefersReducedMotion() ? value : 0));

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion() || !('IntersectionObserver' in window)) {
      setShown(value);
      return undefined;
    }
    let raf = 0;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const tick = (t) => {
        const k = Math.min(1, (t - t0) / 900);
        setShown(Math.round(value * (1 - (1 - k) ** 3)));   // ease-out, no overshoot
        if (k < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.6 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [value]);

  return (
    <span ref={ref} className="lp-metric-value">
      <span aria-hidden="true">{shown.toLocaleString('en-IN')}{suffix}</span>
      <span className="lp-sr">{value.toLocaleString('en-IN')}{suffix}</span>
    </span>
  );
}

export default function ProofStrip() {
  const ref = useReveal();
  return (
    <section className="lp-proof" aria-label="Track record">
      <div ref={ref} className="lp-wrap lp-proof-row lp-reveal">
        <p className="lp-proof-line">Proven on live projects, not demos.</p>
        <ul className="lp-proof-metrics">
          {METRICS.map((m) => (
            <li key={m.placeholder || m.label} className="lp-metric">
              {m.value != null ? (
                <>
                  <CountUp value={m.value} suffix={m.suffix} />
                  <span className="lp-metric-label">{m.label}</span>
                </>
              ) : (
                <Placeholder>{m.placeholder}</Placeholder>
              )}
            </li>
          ))}
        </ul>
        <ul className="lp-proof-logos" aria-label="Projects">
          {LOGOS.map((l) => <li key={l}><Placeholder>{l}</Placeholder></li>)}
        </ul>
      </div>
    </section>
  );
}
